import { THEME_FONT_PAIRS } from '../config'
import catalogue from './googleFonts.json'

/**
 * The typefaces a page can be set in.
 *
 * Curated pairs rather than a font browser: a heading face and a body face that
 * are known to work together, chosen once by somebody who does this for a
 * living. An owner picking two families out of a list of a thousand is how a
 * page ends up in Lobster and Comic Neue.
 *
 * Node-safe on purpose. `renderPage` runs in Node and needs the same families
 * and the same URL the editor uses, so nothing here may touch the DOM.
 */

export type FontPairId = (typeof THEME_FONT_PAIRS)[number]

export interface FontPair {
  id: FontPairId
  /** Google's own spelling of the family. It goes into the URL verbatim. */
  heading: string
  body: string
  /**
   * What the browser uses until the web font arrives, and for good if it never
   * does. A real stack of the same flavour, never a bare `sans-serif`: the
   * page has to keep its character on a slow connection.
   */
  headingFallback: string
  bodyFallback: string
}

export type FontCategory = 'sans' | 'serif' | 'display' | 'handwriting' | 'mono'

export interface FontFamily {
  family: string
  category: FontCategory
  /** The upright weights Google has for it, 100 to 900. */
  weights: number[]
  /**
   * Whether it carries `latin-ext`, which is what ą, ć, ę, ł, ń, ó, ś, ź and ż
   * live in. Around one family in six does not, and in those every Polish word
   * comes out half in the chosen face and half in the fallback.
   */
  polish: boolean
}

const CATEGORIES: Record<string, FontCategory> = {
  s: 'sans',
  r: 'serif',
  d: 'display',
  h: 'handwriting',
  m: 'mono'
}

/**
 * Every family the owner can choose, in popularity order.
 *
 * Built by `scripts/fetch-google-fonts.mjs` from Google's own metadata and then
 * checked family by family against the CSS API, because one family the API
 * refuses fails the whole stylesheet and takes the page's typefaces with it.
 *
 * The whole catalogue, less two kinds of family that would be an option in name
 * only: the ones with no Latin at all, which cannot draw a single letter of a
 * Polish menu, and Google's own brand face, which the API serves to anybody but
 * is licensed to nobody. Families without Polish diacritics are kept and
 * flagged rather than hidden.
 */
export const FONT_FAMILIES: readonly FontFamily[] = (
  catalogue.families as [string, string, string, number?][]
).map(([family, category, weights, noPolish]) => ({
  family,
  category: CATEGORIES[category] ?? 'sans',
  weights: [...weights].map(digit => Number(digit) * 100),
  polish: noPolish !== 0
}))

const FAMILY_BY_NAME = new Map(FONT_FAMILIES.map(item => [item.family, item]))

const SERIF = "Georgia, 'Times New Roman', serif"
const SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif"
const CONDENSED = "'Arial Narrow', 'Helvetica Neue', system-ui, sans-serif"
const ROUNDED = "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif"
const SLAB = "'Rockwell', Georgia, serif"
const SCRIPT = "'Segoe Script', 'Brush Script MT', cursive"
const MONO = "'SFMono-Regular', Consolas, 'Liberation Mono', monospace"

/*
  What a freely chosen family falls back to. By category rather than by name:
  the point of a fallback is to keep the page's character while the web font is
  in flight, so a serif has to fail to a serif.
*/
const FALLBACKS: Record<FontCategory, string> = {
  sans: SANS,
  serif: SERIF,
  display: SANS,
  handwriting: SCRIPT,
  mono: MONO
}

export function familyInfo(family: string): FontFamily | undefined {
  return FAMILY_BY_NAME.get(family)
}

export function fallbackFor(family: string): string {
  const info = familyInfo(family)
  return info ? FALLBACKS[info.category] : SANS
}

/**
 * The weights to request for a family: the regular, and a real bold when the
 * family has one. Asking for a weight a family does not carry is a 400 from the
 * API and no stylesheet at all, so the answer comes from the catalogue rather
 * than from an assumption.
 */
export function requestWeights(family: string): number[] {
  const weights = familyInfo(family)?.weights ?? [400, 700]
  const asked = [400]
  if (weights.includes(700)) asked.push(700)
  else {
    const heavier = weights.filter(weight => weight > 400)
    if (heavier.length > 0) asked.push(Math.max(...heavier))
  }
  return asked
}

/*
  Every family below carries both 400 and 700 on Google Fonts, which is what
  `googleFontsHref` asks for. Adding one that does not would silently serve a
  synthesised bold.
*/
export const FONT_PAIRS: readonly FontPair[] = [
  {
    id: 'classic-serif',
    heading: 'Playfair Display',
    body: 'Lato',
    headingFallback: SERIF,
    bodyFallback: SANS
  },
  {
    id: 'modern-sans',
    heading: 'Montserrat',
    body: 'Inter',
    headingFallback: SANS,
    bodyFallback: SANS
  },
  {
    id: 'condensed-display',
    heading: 'Oswald',
    body: 'Roboto',
    headingFallback: CONDENSED,
    bodyFallback: SANS
  },
  {
    id: 'friendly-rounded',
    heading: 'Quicksand',
    body: 'Nunito',
    headingFallback: ROUNDED,
    bodyFallback: ROUNDED
  },
  {
    id: 'elegant-serif',
    heading: 'Cormorant Garamond',
    body: 'Lato',
    headingFallback: SERIF,
    bodyFallback: SANS
  },
  {
    id: 'editorial-serif',
    heading: 'Libre Baskerville',
    body: 'Source Sans 3',
    headingFallback: SERIF,
    bodyFallback: SANS
  },
  {
    id: 'bistro-script',
    heading: 'Dancing Script',
    body: 'Open Sans',
    headingFallback: SCRIPT,
    bodyFallback: SANS
  },
  {
    id: 'warm-slab',
    heading: 'Bitter',
    body: 'Karla',
    headingFallback: SLAB,
    bodyFallback: SANS
  }
]

const BY_ID = new Map(FONT_PAIRS.map(pair => [pair.id, pair]))

/** Falls back to the first pair, so a document can never render font-less. */
export function fontPair(id: FontPairId): FontPair {
  return BY_ID.get(id) ?? FONT_PAIRS[0]
}

/** `'Playfair Display', Georgia, serif` — quoted only where it has to be. */
export function fontStack(family: string, fallback: string): string {
  return `'${family}', ${fallback}`
}

/**
 * The two faces a theme is actually set in.
 *
 * A pair is the starting point, not the ceiling: `theme.fonts` names families
 * chosen one at a time and wins over the pair for whichever of the two it sets.
 * Same shape as the colours, where `brandColor` derives a palette and
 * `theme.colors` overrides a role at a time.
 */
export function resolveFonts(theme: {
  fontPairId: FontPairId
  fonts?: { heading?: string; body?: string }
}): { heading: string; body: string } {
  const pair = fontPair(theme.fontPairId)
  return {
    heading: theme.fonts?.heading || pair.heading,
    body: theme.fonts?.body || pair.body
  }
}

/**
 * The stack a family is set in: the family itself, then something of the same
 * flavour for while it loads and for if it never does.
 *
 * A family from a pair carries its own hand-picked fallback. A freely chosen
 * one falls back by category, which is the best that can be said about a family
 * nobody looked at.
 */
export function stackFor(family: string): string {
  const pairFallback = FONT_PAIRS.find(
    pair => pair.heading === family
  )?.headingFallback
  const bodyFallback = FONT_PAIRS.find(
    pair => pair.body === family
  )?.bodyFallback
  return fontStack(family, pairFallback ?? bodyFallback ?? fallbackFor(family))
}

/**
 * The Google Fonts stylesheet for the families given, or null for none.
 *
 * One request for all of them: the API takes repeated `family` parameters, and
 * a picker showing thirty specimens at once should cost one round trip rather
 * than thirty. Families are deduplicated and sorted so the URL for a given set
 * is always the same string, which is what makes it cacheable and what lets the
 * editor and the published page share an entry.
 */
export function googleFontsHref(
  families: readonly string[],
  /*
    `regular` is for a list of names, where nothing is ever bold and asking for
    a second face doubles what comes back: 60 families cost 127 kB at one weight
    and 248 kB at two. `full` is for anything the page itself is set in, which
    does have bold text in it.
  */
  weights: 'regular' | 'full' = 'full'
): string | null {
  const wanted = [...new Set(families.filter(Boolean))].sort()
  if (wanted.length === 0) return null

  const query = wanted
    .map(family => {
      const asked = weights === 'regular' ? [400] : requestWeights(family)
      return `family=${family.replace(/ /g, '+')}:wght@${asked.join(';')}`
    })
    .join('&')

  return `https://fonts.googleapis.com/css2?${query}&display=swap`
}

/** The families a set of pairs is drawn in, for the preset tiles. */
export function familiesOfPairs(ids: readonly FontPairId[]): string[] {
  return ids.flatMap(id => [fontPair(id).heading, fontPair(id).body])
}
